/*
 * Outfit Composer - cross-page prewarm
 * -----------------------------------------------------------------------------
 * Warms composed product-card and product-page images ahead of navigation.
 * The expensive bit is Cloudinary generating each tinted derivative; fetching
 * them ahead of time caches them on Cloudinary's edge and in the browser.
 *
 * Strategy:
 *   - Not on a product listing  -> warm page 1 of the all-products listing.
 *   - On a product listing      -> warm CURRENT page cards (live DOM), then
 *                                   page N+1 (real rel="next" link only).
 *   - Product card link intent  -> warm that product page's initial variant
 *                                   composer (hover / pointerdown).
 *   - On listing idle (mobile)  -> warm visible product pages (capped).
 *
 * Include site-wide via snippets/outfit-prewarm.liquid in theme.liquid.
 *
 * Guardrails: idle-only, once per target URL per session, capped + staggered
 * image loads, and disabled under Save-Data / 2G.
 *
 * Optional config (set before this script runs):
 *   window.__ocPrewarm = {
 *     listingUrl: '/collections/all',
 *     maxImages: 64,
 *     maxProductPages: 12,
 *     staggerMs: 120
 *   };
 */
(function () {
  'use strict';

  var cfg = window.__ocPrewarm || {};
  var LISTING_URL = cfg.listingUrl || '/collections/all';
  var MAX_IMAGES = cfg.maxImages || 64;
  var MAX_PRODUCT_PAGES = cfg.maxProductPages || 12;
  var STAGGER_MS = cfg.staggerMs || 120;

  // Respect data-saver and very slow connections.
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  if (conn.saveData) return;
  if (/(^|\b)(slow-)?2g\b/.test(conn.effectiveType || '')) return;

  function idle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      setTimeout(fn, 1200);
    }
  }

  function normPath(p) {
    return String(p || '').replace(/\/+$/, '') || '/';
  }

  function isProductUrl(href) {
    try {
      var u = new URL(href, location.origin);
      return /\/products\/[^/?#]+/.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  // A "listing" page is a collection or search results grid (has the product
  // grid container or an outfit-composer card in the DOM).
  function isListingPage() {
    if (document.querySelector('.oc-card-stack, #ProductGridContainer, .collection, .search')) {
      return true;
    }
    var path = normPath(location.pathname);
    return path.indexOf('/collections') === 0 || path.indexOf('/search') === 0;
  }

  // Resolve the URL of the page we want to warm, or null if there's nothing to do.
  function targetUrl() {
    if (!isListingPage()) return LISTING_URL; // warm page 1 of all-products

    // On a listing: prefer the real "next page" link so we don't invent a page.
    var next = document.querySelector(
      'link[rel="next"], a[rel="next"], .pagination a[aria-label*="next" i], .pagination__item--next a'
    );
    if (next && next.href) return next.href;
    return null; // last/only page — nothing further to warm
  }

  function markOnce(url) {
    try {
      var key = 'oc-prewarm:' + url;
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
    } catch (e) {}
    return true;
  }

  function pushSrc(list, seen, src) {
    if (!src) return;
    src = src.trim();
    if (!/^(https?:)?\/\//.test(src)) return; // only real image URLs (skip data:, blank)
    if (seen[src]) return;
    seen[src] = true;
    list.push(src);
  }

  // Collect composer / card image URLs from a root (live document or parsed page).
  function collectImageUrls(root) {
    var urls = [];
    var seen = {};

    // Composer layers (model + tinted garment plates) - the costly Cloudinary bits.
    root.querySelectorAll('.oc-layer').forEach(function (img) {
      pushSrc(urls, seen, img.getAttribute('src'));
    });
    // Card / stack background images set via inline style url(...).
    root.querySelectorAll('.oc-card-stack[style*="url("], .oc-stack[style*="url("]').forEach(function (el) {
      var m = (el.getAttribute('style') || '').match(/url\((['"]?)([^'")]+)\1\)/);
      if (m) pushSrc(urls, seen, m[2]);
    });
    // Standard product-card images (non-composer cards on the same page).
    root.querySelectorAll('.card__media img').forEach(function (img) {
      var src = img.getAttribute('src');
      if (!src) {
        var ss = img.getAttribute('srcset');
        if (ss) src = ss.split(',')[0].trim().split(' ')[0];
      }
      pushSrc(urls, seen, src);
    });

    return urls;
  }

  function preload(urls, done) {
    urls = urls.slice(0, MAX_IMAGES);
    var i = 0;
    (function next() {
      if (i >= urls.length) {
        if (done) done();
        return;
      }
      var im = new Image();
      im.decoding = 'async';
      im.src = urls[i++];
      setTimeout(next, STAGGER_MS);
    })();
  }

  // Fetch a product page and warm its initial-variant composer images.
  function warmProductPage(url) {
    if (!url || !isProductUrl(url) || !markOnce('pdp:' + url)) return;
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (html) preload(collectImageUrls(new DOMParser().parseFromString(html, 'text/html')));
      })
      .catch(function () {});
  }

  // Warm product pages linked from visible cards (mobile / no-hover fallback).
  function warmVisibleProductPages(root) {
    var seen = {};
    var count = 0;
    root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      if (count >= MAX_PRODUCT_PAGES || !a.href || seen[a.href]) return;
      if (!isProductUrl(a.href)) return;
      seen[a.href] = true;
      count++;
      warmProductPage(a.href);
    });
  }

  // Fetch the next (or first) listing page and warm its card images.
  function warmNextPage() {
    var url = targetUrl();
    if (!url || !markOnce(url)) return;
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (html) preload(collectImageUrls(new DOMParser().parseFromString(html, 'text/html')));
      })
      .catch(function () {});
  }

  function bindProductLinkPrewarm() {
    if (window.__ocPrewarmLinksBound) return;
    window.__ocPrewarmLinksBound = true;

    function fromEvent(e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (a && a.href) warmProductPage(a.href);
    }

    document.addEventListener('pointerenter', fromEvent, true);
    document.addEventListener('pointerdown', fromEvent, true);
  }

  bindProductLinkPrewarm();

  idle(function () {
    // On a listing page, warm the current page's own cards first (covers lazy /
    // below-the-fold products), then chain to the next page and visible PDPs.
    // Elsewhere, warm page 1 of the all-products listing.
    if (isListingPage() && markOnce('current:' + normPath(location.pathname) + location.search)) {
      preload(collectImageUrls(document), function () {
        warmNextPage();
        idle(function () { warmVisibleProductPages(document); });
      });
    } else {
      warmNextPage();
    }
  });
})();
